// File: src/pages/candidate/CompanyListPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../../css/HomePage.css';

// --- Icons SVG ---
const IconUsers = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>;
const IconMapPin = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>;
const IconBriefcase = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></svg>;
const IconSearch = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>;

export default function CompanyListPage() {
    const navigate = useNavigate();
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);

    // ✅ State cho bộ lọc & API Tỉnh thành
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [provinces, setProvinces] = useState([]);
    const location = useLocation();
    const isCandidateRoute = location.pathname.startsWith('/candidate');
    const handleViewDetail = (companyId) => {
        const targetUrl = isCandidateRoute
            ? `/candidate/companies/${companyId}` // Nếu đang ở /candidate -> giữ nguyên
            : `/companies/${companyId}`;          // Nếu đang ở public -> dùng link public
        navigate(targetUrl);
    };

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

    // 1. Fetch dữ liệu Công ty
    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const res = await fetch(`${apiBaseUrl}/api/companies`);
                if (res.ok) {
                    const rawData = await res.json();

                    // ✅ BƯỚC 1: Xử lý dữ liệu & Gắn cờ TOP
                    const processedData = rawData.map(comp => ({
                        ...comp,
                        // Logic TOP: Là gói Premium HOẶC có nhiều job (>5)
                        isTop: comp.subscription?.plan === 'premium' || comp.jobCount >= 5
                    }));

                    // ✅ BƯỚC 2: Sắp xếp (TOP lên đầu -> Job nhiều -> Mới nhất)
                    const sortedData = processedData.sort((a, b) => {
                        // Ưu tiên 1: Công ty TOP (Enterprise)
                        if (b.isTop !== a.isTop) return b.isTop ? 1 : -1;
                        // Ưu tiên 2: Số lượng việc làm giảm dần
                        return (b.jobCount || 0) - (a.jobCount || 0);
                    });

                    setCompanies(sortedData);
                }
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        fetchCompanies();
    }, [apiBaseUrl]);

    // 2. ✅ Fetch danh sách Tỉnh/Thành phố từ Open API
    useEffect(() => {
        const fetchProvinces = async () => {
            try {
                const response = await fetch('https://provinces.open-api.vn/api/?depth=1');
                const data = await response.json();

                // Sắp xếp theo tên cho dễ tìm
                // data format: [{ name: "Thành phố Hà Nội", code: 1, ... }, ...]
                setProvinces(data);
            } catch (error) {
                console.error("Lỗi lấy danh sách tỉnh thành:", error);
            }
        };
        fetchProvinces();
    }, []);

    // 3. ✅ Logic lọc thông minh (Xử lý chuỗi địa điểm)
    const filteredCompanies = companies.filter(comp => {
        // Lọc theo tên hoặc ngành
        const matchName = comp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            comp.industry?.toLowerCase().includes(searchTerm.toLowerCase());

        // Lọc theo địa điểm
        let matchLocation = true;
        if (selectedCity !== '') {
            // Chuẩn hóa chuỗi để so sánh dễ hơn (Bỏ "Thành phố", "Tỉnh")
            // Ví dụ: Chọn "Hồ Chí Minh" sẽ khớp với "TP. Hồ Chí Minh", "Hồ Chí Minh", "Quận 1, Hồ Chí Minh"
            const normalizeLoc = (str) => str ? str.toLowerCase().replace(/thành phố|tỉnh|tp\./g, "").trim() : "";
            const compLoc = normalizeLoc(comp.location);
            const filterLoc = normalizeLoc(selectedCity);

            matchLocation = compLoc.includes(filterLoc);
        }

        return matchName && matchLocation;
    });

    const getInitials = (name) => {
        return name ? name.charAt(0).toUpperCase() : 'C';
    };

    return (
        <div className="homepage-wrapper">
            <main className="bbb">

                {/* HEADER & SEARCH */}
                <div className="search-header-container">
                    <div className="search-bg-decoration"></div>
                    <div className="header-content">
                        <h2 className="page-title">Khám phá Doanh nghiệp hàng đầu</h2>
                        <p className="page-subtitle">Tìm kiếm cơ hội việc làm tại các công ty uy tín nhất</p>

                        {/* Search Box */}
                        <div className="search-box-wrapper">
                            {/* Input Tên */}
                            <div className="search-input-group flex-grow">
                                <span className="search-icon"><IconSearch /></span>
                                <input
                                    type="text"
                                    placeholder="Nhập tên công ty, lĩnh vực..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            <div className="divider"></div>

                            {/* ✅ Select Địa điểm từ API */}
                            <div className="search-input-group location-select">
                                <span className="search-icon"><IconMapPin /></span>
                                <select
                                    value={selectedCity}
                                    onChange={(e) => setSelectedCity(e.target.value)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <option value="">Tất cả địa điểm</option>
                                    {provinces.map((prov) => (
                                        <option key={prov.code} value={prov.name}>
                                            {prov.name.replace("Thành phố ", "").replace("Tỉnh ", "")}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <button className="btn-search-submit">
                                Tìm kiếm
                            </button>
                        </div>
                    </div>
                </div>

                {/* RESULT LIST */}
                <div className="content-container">
                    <div className="results-meta">
                        Tìm thấy <span style={{ fontWeight: 700, color: '#2563EB' }}>{filteredCompanies.length}</span> công ty phù hợp
                    </div>

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 60, color: '#6B7280' }}>
                            <span className="material-symbols-outlined spin">sync</span> Đang tải dữ liệu...
                        </div>
                    ) : (
                        <div className="bento-grid">
                            {filteredCompanies.length > 0 ? (
                                filteredCompanies.map((comp) => (
                                    <div key={comp._id} className="bento-card" style={{ justifyContent: 'flex-start', gap: '16px', position: 'relative' }}>
                                        {comp.isTop && (
                                            <div className="absolute top-0 right-0">
                                                <div className="bg-yellow-400 text-blue-900 text-[10px] font-bold px-3 py-1 rounded-bl-xl rounded-tr-xl flex items-center gap-1 shadow-sm">
                                                    TOP <span className="material-symbols-outlined text-[12px]">workspace_premium</span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Badge Job Count */}
                                        {comp.jobCount > 0 && (
                                            <div className="badge-job-count">
                                                {comp.jobCount} việc làm
                                            </div>
                                        )}

                                        {/* Logo */}
                                        <div className="company-logo-box">
                                            {comp.logo ? (
                                                <img src={comp.logo} alt={comp.name} />
                                            ) : (
                                                getInitials(comp.name)
                                            )}
                                        </div>

                                        {/* Content */}
                                        <div style={{ flex: 1 }}>
                                            <h3 className="company-name-title" onClick={() => handleViewDetail(comp._id)}>
                                                {comp.name}
                                            </h3>

                                            <div className="company-tags">
                                                <span><IconMapPin /> {comp.location || 'Chưa cập nhật'}</span>
                                                <span><IconBriefcase /> {comp.industry}</span>
                                            </div>

                                            <p className="company-desc">
                                                {comp.description || "Công ty chưa cập nhật mô tả."}
                                            </p>
                                        </div>

                                        {/* Footer */}
                                        <div className="card-footer">
                                            <span><IconUsers /> {comp.size} nhân viên</span>
                                        </div>

                                        {/* Button */}
                                        <button
                                            onClick={() => handleViewDetail(comp._id)}
                                            className={`btn-view-profile ${comp.jobCount > 0 ? 'primary' : 'secondary'}`}
                                        >
                                            {comp.jobCount > 0 ? 'Xem vị trí tuyển dụng' : 'Xem hồ sơ'}
                                        </button>
                                    </div>
                                ))
                            ) : (
                                <div className="no-results-box">
                                    <div style={{ fontSize: 40, marginBottom: 16 }}>🏢</div>
                                    <p style={{ fontSize: 16, fontWeight: 600, color: '#374151' }}>Không tìm thấy công ty nào</p>
                                    <p style={{ color: '#6B7280', marginTop: 4 }}>Hãy thử thay đổi từ khóa hoặc bộ lọc địa điểm</p>
                                    <button onClick={() => { setSearchTerm(''); setSelectedCity('') }}>Xóa bộ lọc</button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </main>

            <style>{`
                .homepage-wrapper { background: #F9FAFB; min-height: 100vh; }
                .bbb {
    
    /* Tách xa Navbar hơn */
    padding-bottom: 80px;
    max-width: 1280px;
    width: 100%;
    margin: 0 auto;
    padding-left: 24px;
    padding-right: 24px;
    flex: 1;
    /* Đẩy footer xuống dưới cùng */
}
                
                /* HEADER STYLES */
                .search-header-container {
                    background: linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%);
                    padding: 110px 24px 80px;
                    text-align: center;
                    position: relative;
                    margin-bottom: 40px;
                    /* ✅ THÊM DÒNG NÀY: Bo tròn 2 góc dưới */
                    border-radius: 0 0 40px 40px; 
                    /* Thêm shadow nhẹ để tách biệt với nền dưới */
                    box-shadow: 0 4px 20px -5px rgba(37, 99, 235, 0.1); 
                }
                .page-title { font-size: 28px; font-weight: 800; color: #1E40AF; margin-bottom: 8px; }
                .page-subtitle { color: #4B5563; margin-bottom: 32px; font-size: 16px; }

                /* SEARCH BOX STYLES */
                .search-box-wrapper {
                    background: white;
                    max-width: 800px;
                    margin: 0 auto;
                    border-radius: 16px;
                    padding: 8px;
                    display: flex;
                    align-items: center;
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
                    border: 1px solid #E5E7EB;
                }
                
                .search-input-group {
                    display: flex; align-items: center; padding: 0 16px; position: relative;
                }
                .flex-grow { flex: 1; }
                .location-select { width: 220px; }
                
                .search-icon { color: #9CA3AF; margin-right: 12px; display: flex; }
                
                .search-box-wrapper input, .search-box-wrapper select {
                    width: 100%; border: none; outline: none; font-size: 15px; color: #111827; padding: 12px 0;
                    background: transparent;
                }
                .search-box-wrapper input::placeholder { color: #9CA3AF; }

                .divider { width: 1px; height: 32px; background: #E5E7EB; margin: 0 8px; }

                .btn-search-submit {
                    background: #2563EB; color: white; border: none; padding: 12px 24px;
                    border-radius: 10px; font-weight: 600; cursor: pointer; transition: all 0.2s;
                    min-width: 100px;
                }
                .btn-search-submit:hover { background: #1D4ED8; transform: translateY(-1px); }

                /* CONTENT STYLES */
                .content-container { max-width: 1200px; margin: 0 auto; padding: 0 24px 60px; }
                .results-meta { margin-bottom: 24px; color: #6B7280; font-size: 14px; }

                /* CARD STYLES */
                .badge-job-count {
                    position: absolute; top: 20x; right: 16px;
                    background: #EFF6FF; color: #2563EB; border: 1px solid #BFDBFE;
                    font-size: 11px; font-weight: 700; padding: 4px 10px; border-radius: 20px;
                }
                .company-logo-box {
                    width: 64px; height: 64px; border-radius: 12px;
                    background: white; border: 1px solid #E5E7EB;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 24px; font-weight: bold; color: #374151; overflow: hidden;
                }
                .company-logo-box img { width: 100%; height: 100%; object-fit: contain; }
                
                .company-name-title {
                    font-size: 18px; font-weight: 700; color: #111827; margin: 0 0 6px; cursor: pointer; transition: color 0.2s;
                }
                .company-name-title:hover { color: #2563EB; }

                .company-tags { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
                .company-tags span {
                    font-size: 12px; color: #4B5563; background: #F3F4F6; padding: 4px 8px; border-radius: 6px;
                    display: flex; align-items: center; gap: 4px;
                }
                
                .company-desc {
                    font-size: 14px; color: #6B7280; line-height: 1.5;
                    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
                }

                .card-footer {
                    padding-top: 16px; border-top: 1px solid #E5E7EB; margin-top: auto;
                    font-size: 13px; color: #6B7280; width: 100%;
                }
                .card-footer span { display: flex; align-items: center; gap: 6px; }

                .btn-view-profile {
                    width: 100%; padding: 10px; border-radius: 8px; margin-top: 16px;
                    font-weight: 600; cursor: pointer; transition: all 0.2s;
                }
                .btn-view-profile.primary { background: #2563EB; color: white; border: none; }
                .btn-view-profile.secondary { background: white; color: #374151; border: 1px solid #D1D5DB; }
                .btn-view-profile:hover { opacity: 0.9; transform: translateY(-1px); }

                .no-results-box {
                    grid-column: 1 / -1; text-align: center; padding: 60px; background: white; border-radius: 16px;
                    border: 1px dashed #D1D5DB; margin-top: 20px;
                }
                .no-results-box button {
                    margin-top: 16px; padding: 8px 16px; background: white; border: 1px solid #D1D5DB; 
                    border-radius: 6px; cursor: pointer; font-weight: 500;
                }
                .spin { animation: spin 1s linear infinite; vertical-align: middle; margin-right: 8px; }
                @keyframes spin { 100% { transform: rotate(360deg); } }
                

                /* RESPONSIVE */
                @media (max-width: 768px) {
                    .search-box-wrapper { flex-direction: column; gap: 8px; padding: 16px; }
                    .search-input-group, .location-select { width: 100%; }
                    .divider { display: none; }
                    .btn-search-submit { width: 100%; }
                }
            `}</style>
        </div>
    );
}